"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../supabaseClient";
import type { Session, User } from "@supabase/supabase-js";
import type { Profile } from "../types/types";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userId: string | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Use refs to track subscription state and prevent duplicate subscriptions
  const profileSubscriptionRef = useRef<any>(null);
  const isInitializedRef = useRef(false);

  const fetchProfileInternal = useCallback(
    async (currentUserId: string | null) => {
      if (!currentUserId) {
        setProfile(null);
        return;
      }

      try {
        console.log(
          `[AuthContext] Fetching profile for user: ${currentUserId}`
        );
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", currentUserId)
          .maybeSingle();

        if (error) {
          console.warn(
            `[AuthContext] Error fetching profile: ${error.message}`
          );
          setProfile(null);
          return;
        }

        if (!data) {
          console.warn(
            `[AuthContext] No profile found for user: ${currentUserId}. Profile should be created during signup.`
          );
          setProfile(null);
          return;
        }

        console.log(`[AuthContext] Profile fetched successfully`);
        setProfile(data as Profile);
      } catch (error: any) {
        console.error(
          `[AuthContext] Critical error fetching profile: ${error.message}`
        );
        setProfile(null);
      }
    },
    []
  );

  const refreshProfile = useCallback(async () => {
    if (userId) {
      console.log(
        `[AuthContext] Manually refreshing profile for user: ${userId}`
      );
      await fetchProfileInternal(userId);
    }
  }, [userId, fetchProfileInternal]);

  // Cleanup function for profile subscription
  const cleanupProfileSubscription = useCallback(() => {
    if (profileSubscriptionRef.current) {
      console.log("[AuthContext] Cleaning up profile subscription");
      supabase.removeChannel(profileSubscriptionRef.current);
      profileSubscriptionRef.current = null;
    }
  }, []);

  // Setup profile subscription
  const setupProfileSubscription = useCallback(
    (currentUserId: string) => {
      // Clean up existing subscription first
      cleanupProfileSubscription();

      console.log(
        `[AuthContext] Setting up profile subscription for user: ${currentUserId}`
      );

      profileSubscriptionRef.current = supabase
        .channel(`profile_changes_for_${currentUserId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "profiles",
            filter: `id=eq.${currentUserId}`,
          },
          (payload) => {
            console.log(
              "[AuthContext] Profile updated via realtime:",
              payload.new
            );
            const newProfile = payload.new as Profile;
            setProfile(newProfile);
          }
        )
        .subscribe((status, err) => {
          if (err) {
            console.error("[AuthContext] Realtime subscription error:", err);
          } else {
            console.log(
              `[AuthContext] Realtime subscription status: ${status}`
            );
          }
        });
    },
    [cleanupProfileSubscription]
  );

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const getInitialSession = async () => {
      console.log("[AuthContext] Getting initial session...");
      setLoading(true);

      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        console.log(
          `[AuthContext] Initial session: ${
            initialSession ? "Found" : "Not found"
          }`
        );

        setSession(initialSession);
        const initialUser = initialSession?.user ?? null;
        setUser(initialUser);
        const initialUserId = initialUser?.id ?? null;
        setUserId(initialUserId);

        if (initialUserId) {
          await fetchProfileInternal(initialUserId);
          setupProfileSubscription(initialUserId);
        } else {
          setProfile(null);
        }
      } catch (error: any) {
        console.error("[AuthContext] Error getting initial session:", error);
      } finally {
        console.log("[AuthContext] Initial session loading complete");
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log(`[AuthContext] Auth state change: ${event}`);

        // Skip INITIAL_SESSION as it's handled by getInitialSession
        if (event === "INITIAL_SESSION") {
          return;
        }

        setLoading(true);
        setSession(currentSession);
        const currentUser = currentSession?.user ?? null;
        setUser(currentUser);
        const currentUserId = currentUser?.id ?? null;
        setUserId(currentUserId);

        if (currentUserId) {
          await fetchProfileInternal(currentUserId);
          setupProfileSubscription(currentUserId);
        } else {
          setProfile(null);
          cleanupProfileSubscription();
        }

        setLoading(false);
      }
    );

    return () => {
      console.log("[AuthContext] Cleaning up AuthProvider");
      authListener?.subscription.unsubscribe();
      cleanupProfileSubscription();
      isInitializedRef.current = false;
    };
  }, [
    fetchProfileInternal,
    setupProfileSubscription,
    cleanupProfileSubscription,
  ]);

  return (
    <AuthContext.Provider
      value={{ session, user, userId, profile, loading, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
